import React, { useState, useRef } from 'react'
import SignatureCanvas from 'react-signature-canvas'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import axios from 'axios'
import { browserName, osName } from 'react-device-detect'
import { InputCheckbox } from '../../reusable/inputCheckbox'
import { SignatureActionBar } from '../../reusable/signatureActionBar'
import { FormPartInterface, SaveValueInterface } from '../../../interfaces'
import { useClaimData } from '../../../claimProvider'
import { trackSubmitApplication } from '../../../helpers/handleTracking'
import { DocumentPreviewCard } from '../../reusable/documentPreviewCard'

import styles from './styles.module.scss'
import sigPadStyles from '../../reusable/sigPad/styles.module.scss'

/** Exact copy shown on the checkbox — also sent in the sign payload for audit. */
const CONSENT_DISPLAY_NAME = 'I confirm that I have read and understood the documents below'
const CONSENT_SUPPORTING_TEXT =
    'and authorise Sentinel Legal to apply my signature to them and pursue my hearing loss claim on my behalf.'

export const SignatureHearingLoss = (props: FormPartInterface) => {
    const { saveValueFunct, isActive, assignGroup } = props
    const claim = useClaimData()
    // Only the legacy cohort the backend flags gets the A/B choice. Anything else
    // (including a missing value) shows Option A on its own, so an unexpected
    // response fails toward the new no-choice journey rather than offering a choice.
    const showMatrixChoice = claim?.matrixChoiceEnabled === true
    const [stepData, setStepData] = useState<any>({})
    const [sigData, setSigData] = useState('')
    const [acceptTermsChecked, setAcceptTermsChecked] = useState(false)
    const [matrixOption, setMatrixOption] = useState<'proceed' | 'not-proceed' | null>(showMatrixChoice ? null : 'proceed')
    const [expandedOption, setExpandedOption] = useState<'A' | 'B' | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const signCanvas = useRef<SignatureCanvas>(null) as React.MutableRefObject<any>
    const signatureSectionRef = useRef<HTMLDivElement>(null)
    const consentSectionRef = useRef<HTMLDivElement>(null)
    const matrixSectionRef = useRef<HTMLDivElement>(null)

    console.log('claim.token: ', claim?.token)

    const preSaveValueFunct = (dataToSave: SaveValueInterface) => {
        const saved = saveValueFunct(dataToSave)
        const tempStepData = { ...stepData, [dataToSave.field]: dataToSave.value }
        setStepData(tempStepData)
        if (dataToSave.field === 'acceptTerms') {
            setAcceptTermsChecked(dataToSave.value === 'yes')
        }
        return saved
    }

    const onAcceptTermsChange = (checked: boolean) => {
        setAcceptTermsChecked(checked)
        preSaveValueFunct({ field: 'acceptTerms', value: checked ? 'yes' : 'no' })
    }

    const saveSign = () => {
        signCanvas.current.on()
        setSigData(signCanvas.current.toDataURL('image/png'))
    }

    const clearSign = (e: React.MouseEvent) => {
        e.preventDefault()
        signCanvas.current.clear()
        signCanvas.current.on()
        setSigData('')
    }

    const hasSignature = sigData.length > 0
    const allConsentsConfirmed = acceptTermsChecked
    const formReady = hasSignature && allConsentsConfirmed && Boolean(matrixOption)
    const consentProgressLabel = `${acceptTermsChecked ? 1 : 0} of 1 confirmed`
    const blockedMessage = !hasSignature
        ? 'Please add your signature above to continue.'
        : !acceptTermsChecked
          ? 'Please confirm you have read and understood the documents.'
          : !matrixOption
            ? 'Please select Option A or Option B.'
            : null

    const scrollToIncomplete = () => {
        const target = !hasSignature
            ? signatureSectionRef.current
            : !acceptTermsChecked
              ? consentSectionRef.current
              : matrixSectionRef.current
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    const submitSignature = () => {
        if (!formReady) {
            scrollToIncomplete()
            return
        }

        setIsSubmitting(true)
        Loading.circle('Signing Document')

        axios
            .post(`${process.env.REACT_APP_API_UPLOAD_URL}`, {
                type: 'mhl-esign-signature',
                token: claim?.token,
                sigData,
                proceed: matrixOption === 'proceed',
                browser: browserName,
                device: osName,
                consent: {
                    checked: acceptTermsChecked === true,
                    displayName: CONSENT_DISPLAY_NAME,
                    supportingText: CONSENT_SUPPORTING_TEXT,
                },
            })
            .then((response) => {
                Loading.remove()
                setIsSubmitting(false)
                if (!response?.data?.success && response?.data !== 'SUCCESS') {
                    Report.failure(
                        'An Error Occurred',
                        'We have encountered an internal error, please try again later. If the problem persists please contact support@sentinellegal.co.uk',
                        'Okay'
                    )
                    return
                }
                trackSubmitApplication()
                Report.success(
                    'Document Signed',
                    'Thank you for signing your document. We will update you on your case in due course.',
                    'Okay',
                    () => {
                        window.location.href = 'https://sentinellegal.co.uk'
                    }
                )
            })
            .catch((error) => {
                console.log(error)
                Loading.remove()
                setIsSubmitting(false)
                Report.failure(
                    'An Error Occurred',
                    'We have encountered an internal error, please try again later. If the problem persists please contact support@sentinellegal.co.uk',
                    'Okay'
                )
            })
    }

    const packDocuments = Array.isArray(claim?.documents) ? claim.documents.filter((d: { url?: string }) => d?.url) : []
    const hasPack = packDocuments.length > 0
    if (isActive !== 'signature' || (!hasPack && !claim?.documentUrl)) {
        return <></>
    }

    const isLimitation = claim?.claimType === 'limitation'
    const heading = isLimitation
        ? 'Sign your hearing loss claim (Limitation)'
        : 'Sign your hearing loss claim (Non-Limitation)'

    interface OptionSection { label: string; bullets: string[]; isWarning?: boolean }
    interface OptionDetails { intro: string; sections: OptionSection[] }

    const optionADetails: OptionDetails = isLimitation
        ? {
              intro: 'Your claim is progressed through the Matrix Scheme — a structured process agreed between claimant representatives and the Ministry of Defence.',
              sections: [
                  {
                      label: 'Why this is recommended for your case:',
                      bullets: [
                          'The Ministry of Defence will not argue that your claim is out of time (limitation). Without the Matrix, this is very likely to cause your claim to fail entirely.',
                          'Liability, duty of care, and noise exposure will not be contested.',
                          'Claims typically settle within 6–18 months, compared to several years through the courts.',
                          'Your claim will be assessed on its medical merits, not dismissed on technical grounds.',
                      ],
                  },
                  {
                      label: 'Things to be aware of:',
                      bullets: [
                          'Compensation is reduced by an agreed percentage (10–30%) compared to what might be achieved in a fully successful court case.',
                          'Awards are based on agreed valuation bands rather than a fully individual assessment.',
                      ],
                  },
              ],
          }
        : {
              intro: 'Your claim is progressed through the Matrix Scheme — a structured process agreed between claimant representatives and the Ministry of Defence.',
              sections: [
                  {
                      label: 'Why this may suit you:',
                      bullets: [
                          'The Ministry of Defence will not contest liability, duty of care, noise exposure, or limitation.',
                          'Claims typically settle within 6–18 months, compared to several years through the courts.',
                          'The process is more predictable — your claim is assessed against agreed criteria and valuation bands.',
                          'Legal costs are generally lower due to the streamlined process.',
                      ],
                  },
                  {
                      label: 'Things to be aware of:',
                      bullets: [
                          'Compensation may be 10–30% lower than could be achieved in a fully successful court case.',
                          'Awards are based on standardised valuation bands rather than full individual assessment.',
                          'Once in the Matrix, it can be difficult to switch to the court route.',
                      ],
                  },
              ],
          }

    const optionBDetails: OptionDetails = isLimitation
        ? {
              intro: 'Your claim proceeds through the standard court (litigation) process instead of the Matrix Scheme.',
              sections: [
                  {
                      label: 'Important — please read carefully:',
                      isWarning: true,
                      bullets: [
                          'As your claim is limitation-affected, the Ministry of Defence is very likely to argue it is out of time.',
                          'If the court accepts that argument, your claim will be dismissed and you will receive no compensation at all.',
                          'Sentinel Legal strongly recommends Option A for clients in your situation.',
                      ],
                  },
                  {
                      label: 'What this route involves:',
                      bullets: [
                          'Your claim would proceed through court proceedings, which typically take several years.',
                          'The Ministry of Defence will contest liability and other issues.',
                          'If limitation is successfully overcome and the claim succeeds, compensation could be higher than through the Matrix — but the risk of recovering nothing is significant.',
                      ],
                  },
              ],
          }
        : {
              intro: 'Your claim proceeds through the standard court (litigation) process instead of the Matrix Scheme.',
              sections: [
                  {
                      label: 'What this route involves:',
                      bullets: [
                          'The Ministry of Defence may contest liability, limitation, and other aspects of your claim.',
                          'The process typically takes several years to resolve.',
                          'If fully successful, compensation could be higher than through the Matrix.',
                          'There is a greater risk of recovering nothing if the Ministry of Defence successfully defends your claim.',
                          'Legal costs and complexity are generally higher than through the Matrix.',
                      ],
                  },
              ],
          }

    const toggleExpanded = (option: 'A' | 'B') => {
        setExpandedOption((current) => (current === option ? null : option))
    }

    return (
        <div className={`${styles.container} ${styles.withActionBar}`}>
            <div className={styles.constrained}>
                <h2 className={styles.heading}>{heading}</h2>
                <p className={styles.documentsSubtitle}>
                    Please provide your signature below, then carefully review {hasPack ? 'your documents' : 'your claim document'}.
                    {(claim?.first_name || claim?.last_name)
                        ? <> {hasPack ? 'These documents have' : 'This document has'} been populated with the details we hold for <span className={styles.clientName}>{`${claim.first_name} ${claim.last_name}`.trim()}</span>.</>
                        : ''}
                </p>
            </div>

            <div
                ref={signatureSectionRef}
                className={`${sigPadStyles.signaturePadContent} ${styles.constrained}`}
            >
                <div className={sigPadStyles.sigPadContainer}>
                    <p>Please use your mouse, finger or stylus to draw your signature below.</p>
                    <div>
                        <SignatureCanvas
                            ref={signCanvas}
                            onEnd={saveSign}
                            clearOnResize={false}
                            penColor="grey"
                            canvasProps={{
                                width: window.innerWidth < 760 ? 350 : 760,
                                height: 300,
                                className: 'sigCanvas',
                            }}
                        />
                        <span onClick={clearSign} style={{ cursor: 'pointer' }}>Clear</span>
                    </div>
                </div>

                <div
                    ref={consentSectionRef}
                    style={{ marginBottom: '1rem', marginTop: '1rem' }}
                    className={styles.checkboxWrapper}
                >
                    <InputCheckbox
                        id="mhl-accept-terms"
                        displayName={CONSENT_DISPLAY_NAME}
                        supportingText={CONSENT_SUPPORTING_TEXT}
                        value={acceptTermsChecked}
                        onChange={onAcceptTermsChange}
                    />
                </div>

                <p className={sigPadStyles.signatureDisclaimer} style={{ textAlign: 'left' }}>
                    By signing this document, you confirm the information above is accurate and you authorise
                    Sentinel Legal to act on your behalf in pursuing your military hearing loss claim. A signed copy
                    of this document will be emailed to you and stored securely against your case reference.
                </p>
            </div>

            <div ref={matrixSectionRef} className={styles.constrained}>
              {!showMatrixChoice ? (
                <div className={`${styles.matrixOptions} ${styles.lockedOptionBox}`}>
                    <p className={styles.detailsIntro}>{optionADetails.intro}</p>
                    {optionADetails.sections.map((section) => (
                        <div
                            key={section.label}
                            className={section.isWarning ? styles.detailsSectionWarning : styles.detailsSection}
                        >
                            <p className={styles.detailsSectionLabel}>{section.label}</p>
                            <ul>
                                {section.bullets.map((bullet) => (
                                    <li key={bullet}>{bullet}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
              ) : (
                <div className={styles.matrixOptions}>
                    <p className={styles.matrixLabel}>Please select one of the following options:</p>
                    <p className={styles.matrixHint}>
                        See the documents below for full details on the Matrix Scheme, or expand the summary below each option.
                    </p>

                    <div className={styles.matrixOptionWrapper}>
                        <label className={`${styles.matrixOption} ${matrixOption === 'proceed' ? styles.matrixOptionSelected : ''}`}>
                            <input
                                type="radio"
                                name="matrixOption"
                                value="proceed"
                                checked={matrixOption === 'proceed'}
                                onChange={() => setMatrixOption('proceed')}
                            />
                            <span className={styles.matrixOptionText}>
                                <strong>Option A</strong> — Proceed via the Matrix Scheme
                            </span>
                        </label>
                        <button
                            type="button"
                            className={styles.detailsToggle}
                            aria-expanded={expandedOption === 'A'}
                            aria-controls="matrix-option-a-details"
                            onClick={() => toggleExpanded('A')}
                        >
                            {expandedOption === 'A' ? 'Hide details' : 'What does this mean?'}
                        </button>
                        {expandedOption === 'A' && (
                            <div id="matrix-option-a-details" className={styles.detailsPanel}>
                                <p className={styles.detailsIntro}>{optionADetails.intro}</p>
                                {optionADetails.sections.map((section) => (
                                    <div key={section.label} className={section.isWarning ? styles.detailsSectionWarning : styles.detailsSection}>
                                        <p className={styles.detailsSectionLabel}>{section.label}</p>
                                        <ul>
                                            {section.bullets.map((bullet) => (
                                                <li key={bullet}>{bullet}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className={styles.matrixOptionWrapper}>
                        <label className={`${styles.matrixOption} ${matrixOption === 'not-proceed' ? styles.matrixOptionSelected : ''}`}>
                            <input
                                type="radio"
                                name="matrixOption"
                                value="not-proceed"
                                checked={matrixOption === 'not-proceed'}
                                onChange={() => setMatrixOption('not-proceed')}
                            />
                            <span className={styles.matrixOptionText}>
                                <strong>Option B</strong> — Do NOT Proceed via the Matrix Scheme
                            </span>
                        </label>
                        <button
                            type="button"
                            className={styles.detailsToggle}
                            aria-expanded={expandedOption === 'B'}
                            aria-controls="matrix-option-b-details"
                            onClick={() => toggleExpanded('B')}
                        >
                            {expandedOption === 'B' ? 'Hide details' : 'What does this mean?'}
                        </button>
                        {expandedOption === 'B' && (
                            <div id="matrix-option-b-details" className={styles.detailsPanel}>
                                <p className={styles.detailsIntro}>{optionBDetails.intro}</p>
                                {optionBDetails.sections.map((section) => (
                                    <div key={section.label} className={section.isWarning ? styles.detailsSectionWarning : styles.detailsSection}>
                                        <p className={styles.detailsSectionLabel}>{section.label}</p>
                                        <ul>
                                            {section.bullets.map((bullet) => (
                                                <li key={bullet}>{bullet}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
              )}
            </div>

            <div className={styles.documentsSection}>
                {hasPack ? (
                    packDocuments.map((doc: { id: string; title: string; url?: string; description?: string }) => (
                        <div className={styles.documentItem} key={doc.id}>
                            <DocumentPreviewCard
                                title={doc.title}
                                description={
                                    doc.description || 'Please review this document carefully before signing.'
                                }
                                url={doc.url}
                                actionLabel={doc.title}
                            />
                        </div>
                    ))
                ) : (
                    <div className={styles.documentItem}>
                        <DocumentPreviewCard
                            title="Hearing Loss Claim Document"
                            description="Your military hearing loss claim pack. Please review it carefully before signing."
                            url={claim.documentUrl}
                            actionLabel="Hearing loss claim"
                        />
                    </div>
                )}
            </div>

            <SignatureActionBar
                onSubmit={submitSignature}
                isSubmitBlocked={!formReady}
                blockedMessage={blockedMessage}
                isSubmitLoading={isSubmitting}
                consentProgressLabel={consentProgressLabel}
                hasSignature={hasSignature}
                allConsentsConfirmed={allConsentsConfirmed}
            />
        </div>
    )
}
